import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MatchResult from "@/components/MatchResult";
import { api, CITIES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";

const SKILLS = [
  { v: "Desain", k: "skill_desain" },
  { v: "Logistik", k: "skill_logistik" },
  { v: "Pengajaran", k: "skill_pengajaran" },
  { v: "Legal/Advokasi", k: "skill_legal" },
  { v: "Media/Komunikasi", k: "skill_media" },
  { v: "Medis/Kesehatan", k: "skill_medis" },
  { v: "Tenaga Lapangan", k: "skill_lapangan" },
];
const AVAILABILITY = [
  { v: "Insidental 1 Hari", k: "avail_1" },
  { v: "Akhir Pekan", k: "avail_2" },
  { v: "2-4 Jam/Minggu", k: "avail_3" },
  { v: "Proyek Jangka Panjang", k: "avail_4" },
];
const FOCUS = [
  { v: "Lingkungan & Sampah", k: "focus_1" },
  { v: "Pendidikan & Anak", k: "focus_2" },
  { v: "Kemiskinan & Pangan", k: "focus_3" },
  { v: "Kesetaraan Sosial & Gender", k: "focus_4" },
  { v: "Bencana Alam", k: "focus_5" },
];

const STEP_KEYS = ["mm_step1", "mm_step2", "mm_step3", "mm_step4"];

function OptionPill({ selected, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 rounded-2xl border-2 font-bold text-sm transition-colors text-left ${
        selected ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function MatchmakerPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [skills, setSkills] = useState([]);
  const [availability, setAvailability] = useState("");
  const [focus, setFocus] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleSkill = (s) => setSkills((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const canNext = [skills.length > 0, !!availability, !!focus, !!city][step];

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/matchmaker", { skills, availability, focus, city });
      setResult(data);
    } catch {
      toast.error("Gagal memproses. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    try {
      await api.post("/saved-plans", {
        profile_title: result.profile_title,
        event_titles: result.events.map((e) => e.title),
        user_id: user?.id || "demo-andini",
      });
      toast.success("Rencana Aksi tersimpan di Profil & Dampak Saya.");
    } catch {
      toast.error("Gagal menyimpan rencana.");
    }
  };

  const reset = () => { setResult(null); setStep(0); setSkills([]); setAvailability(""); setFocus(""); setCity(""); };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="matchmaker-loading">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
        <h2 className="font-heading font-black text-2xl text-slate-900 mt-6">{t("mm_loading")}</h2>
        <p className="text-slate-500 mt-2">{t("mm_loading_sub")}</p>
      </div>
    );
  }

  if (result) {
    return <MatchResult result={result} onSave={savePlan} onReset={reset} />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full">
        <Sparkles className="w-3.5 h-3.5" /> {t("mm_badge")}
      </span>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-4">{t("mm_title")}</h1>
      <p className="text-slate-500 mt-2">{t("mm_sub")}</p>

      <div className="flex gap-2 mt-8">
        {STEP_KEYS.map((k, i) => (
          <div key={k} className="flex-1 min-w-0">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-emerald-600" : "bg-slate-200"}`} />
            <p className={`text-[10px] font-bold mt-1.5 uppercase tracking-wide ${i === step ? "text-emerald-700" : "text-slate-400"}`}>{t(k)}</p>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-7 mt-8"
          data-testid={`wizard-step-${step + 1}`}
        >
          {step === 0 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-1">{t("mm_q1")}</h2>
              <p className="text-sm text-slate-500 mb-5">{t("mm_q1_sub")}</p>
              <div className="grid grid-cols-2 gap-3">
                {SKILLS.map((s, i) => (
                  <OptionPill key={s.v} testid={`skill-option-${i}`} selected={skills.includes(s.v)} onClick={() => toggleSkill(s.v)}>{t(s.k)}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">{t("mm_q2")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABILITY.map((a, i) => (
                  <OptionPill key={a.v} testid={`availability-option-${i}`} selected={availability === a.v} onClick={() => setAvailability(a.v)}>{t(a.k)}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">{t("mm_q3")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FOCUS.map((f, i) => (
                  <OptionPill key={f.v} testid={`focus-option-${i}`} selected={focus === f.v} onClick={() => setFocus(f.v)}>{t(f.k)}</OptionPill>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-5">{t("mm_q4")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CITIES.map((c, i) => (
                  <OptionPill key={c} testid={`city-option-${i}`} selected={city === c} onClick={() => setCity(c)}>{c}</OptionPill>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-6">
        <Button data-testid="wizard-back-btn" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-full font-bold">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t("mm_back")}
        </Button>
        {step < 3 ? (
          <Button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7">
            {t("mm_next")} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid="wizard-submit-btn" disabled={!canNext} onClick={submit} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7">
            <Sparkles className="w-4 h-4 mr-2" /> {t("mm_submit")}
          </Button>
        )}
      </div>
    </div>
  );
}
